import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import useStores from '$src/hooks/useStores';
import { NotMonitorableIcon, SecureProtectionIcon, SettingsGearIcon, SetupCompleteIcon } from '@ring-icons';

import SummaryCard from './SummaryCard';
import './index.less';

const DEFAULT_CARD_SELECTION = 0;

const useCardInteraction = (onChangeFilter) => {
  const [cardSelected, setCardSelected] = useState(DEFAULT_CARD_SELECTION);
  const [anyCardHovered, setAnyCardHovered] = useState(false);

  const onSelect = useCallback(
    (idCard, locations) => {
      const newSelection = idCard === cardSelected ? DEFAULT_CARD_SELECTION : idCard;
      setCardSelected(newSelection);

      if (onChangeFilter) {
        onChangeFilter(newSelection === DEFAULT_CARD_SELECTION ? null : locations);
      }
    },
    [cardSelected, onChangeFilter],
  );

  return { cardSelected, anyCardHovered, setAnyCardHovered, onSelect };
};

function LocationsSummary({ onChangeFilter }) {
  const {
    locationStatusStore: { eligibleLocations, ineligibleLocations, unmonitoringLocations, monitoringLocations },
  } = useStores();

  const cardData = useMemo(
    () => [
      {
        id: 1,
        title: 'Locations set up successfully',
        Icon: SetupCompleteIcon,
        getLocations: () => eligibleLocations,
        danger: false,
      },
      {
        id: 2,
        title: 'Locations not set up successfully',
        Icon: SettingsGearIcon,
        getLocations: () => ineligibleLocations,
        danger: true,
      },
      {
        id: 3,
        title: 'Locations actively monitoring',
        Icon: SecureProtectionIcon,
        getLocations: () => monitoringLocations,
        danger: false,
      },
      {
        id: 4,
        title: 'Locations not actively monitoring',
        Icon: NotMonitorableIcon,
        getLocations: () => unmonitoringLocations,
        danger: true,
      },
    ],
    [eligibleLocations, ineligibleLocations, unmonitoringLocations, monitoringLocations],
  );

  const { cardSelected, anyCardHovered, setAnyCardHovered, onSelect } = useCardInteraction(onChangeFilter);

  return (
    <div
      data-testid="locations-summary"
      className="locations-summary"
      onMouseEnter={() => setAnyCardHovered(true)}
      onMouseLeave={() => setAnyCardHovered(false)}
    >
      <div className="locations-summary__row">
        {cardData.map(({ id, title, Icon, getLocations, danger }) => {
          const locations = getLocations();
          return (
            <SummaryCard
              key={id}
              title={title}
              Icon={Icon}
              amount={locations ? locations.length : 0}
              success={!danger}
              danger={danger}
              isSelected={cardSelected === id}
              hasOpacity={anyCardHovered || cardSelected !== DEFAULT_CARD_SELECTION}
              onSelect={() => onSelect(id, locations)}
            />
          );
        })}
      </div>
    </div>
  );
}

LocationsSummary.propTypes = {
  onChangeFilter: PropTypes.func,
};

export default LocationsSummary;
